// Below-player info bar: title, author, status, viewers, uptime, share, description, owner controls.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock, Download, Share2 } from "lucide-react";
import { api } from "../api/client.js";
import type { StreamDetailDTO } from "../api/types.js";
import { Avatar } from "./ui/Avatar.js";
import { LiveBadge, PausedBadge, StatusBadge } from "./ui/LiveBadge.js";
import { ViewerCount } from "./ui/ViewerCount.js";
import { formatUptime } from "./ui/format.js";
import { Button } from "./ui/button.js";

export interface StreamInfoBarProps {
  stream: StreamDetailDTO;
  viewers: number;
}

export function StreamInfoBar({ stream, viewers }: StreamInfoBarProps) {
  const { t } = useTranslation();
  const [uptime, setUptime] = useState(() => formatUptime(stream.startedAt));
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Live uptime ticker.
  useEffect(() => {
    if (stream.status !== "live" || !stream.startedAt) {
      setUptime(formatUptime(stream.startedAt));
      return;
    }
    setUptime(formatUptime(stream.startedAt));
    const t = setInterval(() => setUptime(formatUptime(stream.startedAt)), 1000);
    return () => clearInterval(t);
  }, [stream.status, stream.startedAt]);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const readyRecording = (stream.recordings ?? []).find((r) => r.status === "ready");
  const showDownload = stream.isOwner && !!readyRecording;

  return (
    <div className="surface p-4 sm:p-5">
      <h1 className="font-display text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
        {stream.title}
      </h1>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <Avatar src={stream.author.avatar} name={stream.author.name} size={36} />
          <span className="text-sm font-medium text-foreground">{stream.author.name}</span>
        </div>

        {stream.status === "live" ? (
          <LiveBadge />
        ) : stream.status === "paused" ? (
          <PausedBadge />
        ) : (
          <StatusBadge status={stream.status} />
        )}

        {stream.status === "live" && <ViewerCount count={viewers} />}

        {stream.status === "live" && uptime && (
          <span
            className="inline-flex items-center gap-1.5 font-mono text-xs tabular-nums text-muted-foreground"
            title={t("info.uptime")}
          >
            <Clock className="h-3.5 w-3.5" />
            {uptime}
          </span>
        )}

        {stream.status === "live" && stream.liveNode && (
          <span className="text-xs text-muted-foreground/60">
            {stream.liveNode.region} · {stream.liveNode.name}
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/* Read-only: recording is fixed at create time (drives the CF recording mode). */}
          {stream.isOwner && stream.recordEnabled && (
            <span
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
              title={t("info.record")}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              {t("info.record")}
            </span>
          )}
          {showDownload && (
            <Button asChild variant="secondary" size="sm">
              <a href={api.url(`/api/streams/${stream.uuid}/recording/download`)}>
                <Download className="h-4 w-4" />
                {t("info.download")}
              </a>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={share}>
            {copied ? <Check className="h-4 w-4 text-primary" /> : <Share2 className="h-4 w-4" />}
            {copied ? t("info.copied") : t("info.share")}
          </Button>
        </div>
      </div>

      {stream.description && (
        <div className="mt-3 border-t border-white/[0.06] pt-3">
          <p
            className={
              expanded
                ? "whitespace-pre-wrap text-sm text-muted-foreground"
                : "line-clamp-2 whitespace-pre-wrap text-sm text-muted-foreground"
            }
          >
            {stream.description}
          </p>
          {stream.description.length > 120 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-xs font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
            >
              {expanded ? t("info.showLess") : t("info.showMore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
